import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './NewsandUpdates.css';

// Import Images
import lmg1 from '../../Assets/Nw1.png';
import lmg2 from '../../Assets/Nw2.png';
import lmg3 from '../../Assets/Nw3.png';
import lmg4 from '../../Assets/Nw4.png';

const newsData = [
  {
    id: 1,
    image: lmg1,
    topic: 'Recent Update',
    description: `Recent updates in taxation include changes in income tax slabs, revised GST rates on essential and luxury goods, increased tax compliance measures using AI-driven monitoring, and updated TDS/TCS provisions for digital transactions. These updates aim to improve transparency, widen the tax base, and simplify the tax structure for individuals and businesses.`,
  },
  {
    id: 2,
    image: lmg2,
    topic: 'Technology Update',
    description: `Taxation technology is rapidly evolving with the integration of AI, blockchain, and automation. Governments and businesses are adopting digital tax platforms, e-invoicing systems, and real-time data analytics to enhance compliance, reduce fraud, and streamline tax filing. Cloud-based solutions and API integrations are enabling seamless tax reporting, while AI-driven tools are helping identify anomalies and improve audit efficiency.`,
  },
  {
    id: 3,
    image: lmg3,
    topic: 'Company Updates',
    description: `We have now arrived in Madampe, a charming town known for its serene landscapes, rich cultural heritage, and welcoming atmosphere.`,
  },
  {
    id: 4,
    image: lmg4,
    topic: 'General News',
    description: `Governments worldwide are revising tax policies to address economic challenges, digital business growth, and sustainability goals. Key updates include increased enforcement on high-income earners, new digital services taxes, and efforts to close corporate tax loopholes. Many countries are also considering green taxes to fund climate initiatives.`,
  },
  
];

const NewsandUpdates = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedNews, setSelectedNews] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [cardsPerView, setCardsPerView] = useState(4);
  const scrollContainerRef = useRef(null);

  // Show arrows only if there are more than 4 cards
  const showArrows = newsData.length > 4;

  // Calculate cards per view based on screen size
  useEffect(() => {
    const updateCardsPerView = () => {
      const width = window.innerWidth;
      if (width < 480) {
        setCardsPerView(1);
      } else if (width < 768) {
        setCardsPerView(2);
      } else if (width < 1024) {
        setCardsPerView(3);
      } else {
        setCardsPerView(4);
      }
    };

    updateCardsPerView();
    window.addEventListener('resize', updateCardsPerView);
    return () => window.removeEventListener('resize', updateCardsPerView);
  }, []);

  const maxIndex = Math.max(0, newsData.length - cardsPerView);

  const scrollToIndex = (index) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = container.scrollWidth / newsData.length;
      const scrollPosition = index * cardWidth;
      
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  const handlePrevious = () => {
    const newIndex = Math.max(0, currentIndex - 1);
    setCurrentIndex(newIndex);
    scrollToIndex(newIndex);
  };

  const handleNext = () => {
    const newIndex = Math.min(maxIndex, currentIndex + 1);
    setCurrentIndex(newIndex);
    scrollToIndex(newIndex);
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = container.scrollWidth / newsData.length;
      const scrollLeft = container.scrollLeft;
      const newIndex = Math.round(scrollLeft / cardWidth);
      setCurrentIndex(Math.max(0, Math.min(newIndex, maxIndex)));
    }
  };

  const openModal = (newsItem) => setSelectedNews(newsItem);
  const closeModal = () => setSelectedNews(null);

  // Calculate which dots to show based on cards per view
  const totalDots = Math.ceil(newsData.length / cardsPerView);

  return (
    <div className="news-wrapper">
      <h2 className="news-heading">News and Updates</h2>
      
      <div className="carousel-wrapper">
        {showArrows && (
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className={`nav-btn left ${currentIndex === 0 ? 'disabled' : ''}`}
            aria-label="Previous"
          >
            <ChevronLeft size={24} />
          </button>
        )}

        <div 
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="cards-container"
        >
          {newsData.map((item) => (
            <div 
              key={item.id} 
              className={`news-card ${hoveredCard === item.id ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredCard(item.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="image-container">
                <img src={item.image} alt={item.topic} />
                <div className="image-overlay" />
              </div>
              <div className="card-content">
                <h3 className="card-title">{item.topic}</h3>
                <p className="card-description">{item.description}</p>
                <button
                  onClick={() => openModal(item)}
                  className="read-more"
                >
                  Read more →
                </button>
              </div>
            </div>
          ))}
        </div>

        {showArrows && (
          <button
            onClick={handleNext}
            disabled={currentIndex === maxIndex}
            className={`nav-btn right ${currentIndex === maxIndex ? 'disabled' : ''}`}
            aria-label="Next"
          >
            <ChevronRight size={24} />
          </button>
        )}
      </div>

      {newsData.length > cardsPerView && (
        <div className="dot-indicators">
          {Array.from({ length: totalDots }, (_, index) => (
            <button
              key={index}
              onClick={() => {
                const newIndex = index * cardsPerView;
                setCurrentIndex(Math.min(newIndex, maxIndex));
                scrollToIndex(Math.min(newIndex, maxIndex));
              }}
              className={`dot ${Math.floor(currentIndex / cardsPerView) === index ? 'active' : ''}`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {selectedNews && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-image">
              <img src={selectedNews.image} alt={selectedNews.topic} />
            </div>
            <div className="modal-content">
              <h3 className="modal-title">{selectedNews.topic}</h3>
              <p className="modal-description">{selectedNews.description}</p>
              <button className="modal-action" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsandUpdates;